// 場面の切り替えで、次に出す絵が読み終わるまで黒のまま待つ (11-01・docs/research/2026-11-01-scene-transition-glitches.md の案1の残り)。
//   10-31 作者「場面が切り替わるタイミングで表示されなかったり、過去の軽い絵の残骸が出たりする」。
//   ①戦いへ抜ける暗転 (ADV_BATTLE_EXIT_MS) が終わっても一戦の絵 (ボス・主人公の Spine・部屋の背景) が読めていなければ、黒のまま待つ。
//   ②会話場面 (幕間・戦闘前) の頭で、最初に出す絵 (と先代の回想の4枚) が読めていなければ、黒のまま場面を始めない (11-02)。
//   ③会話場面を通らずに戦いへ入る時 (見た場面の後の「続きから」・幕間の後に戦闘前の場面が無いボス・一戦だけ) も、一戦の絵が読めるまで黒のまま待つ (11-03・作者 memo「ボスの切り替わりでも出す」)。
//   回線が遅い・読めない絵がある時に止まり続けないよう、延ばすのはどちらも BATTLE_ASSET_WAIT_MAX_MS まで。
//   判定は main が描く側の読み込みを見て渡す。渡されていなければ待たない (テスト・ヘッドレス)。
import type { AdvRuntime, AdvScene } from './adv'

export const BATTLE_ASSET_WAIT_MAX_MS = 6000

let readyCheck: ((bossId: string) => boolean) | null = null
let advReadyCheck: ((scene: AdvScene) => boolean) | null = null

export function setBattleAssetsReadyCheck(fn: ((bossId: string) => boolean) | null): void {
  readyCheck = fn
}
export function setAdvAssetsReadyCheck(fn: ((scene: AdvScene) => boolean) | null): void {
  advReadyCheck = fn
}
// 開発版の ?loadingHold=<秒>: 手元の速い読み込みでも待ちを作り、読み込み待ちの表示を見られるようにする (11-03・src/loading_indicator.ts)。
//   上限 BATTLE_ASSET_WAIT_MAX_MS は変えない。配布版では 0 のまま。
let fakeLoadMs = 0
export function setFakeLoadMs(ms: number): void {
  fakeLoadMs = Number.isFinite(ms) && ms > 0 ? Math.min(ms, BATTLE_ASSET_WAIT_MAX_MS) : 0
}

/** 暗転の経過 exitMs で戦いへ入ってよいか。暗転が終わり、絵が読めたか待ちの上限を過ぎたら true。 */
export function battleExitDone(exitMs: number, exitLenMs: number, bossId: string): boolean {
  if (exitMs < exitLenMs) return false
  if (exitMs >= exitLenMs + BATTLE_ASSET_WAIT_MAX_MS) return true
  if (exitMs < exitLenMs + fakeLoadMs) return false
  return readyCheck ? readyCheck(bossId) : true
}

/**
 * 会話場面を通らずに戦いへ入る時の待ち (GameState.battleEntryWaitMs)。読めたか上限を過ぎたら null (待ち終わり)、まだなら延ばした待ちを返す。
 * 判定が無ければ (テスト・ヘッドレス) すぐ null。
 */
export function battleEntryStep(waitedMs: number, dtMs: number, bossId: string): number | null {
  if (!readyCheck) return null
  if (waitedMs >= BATTLE_ASSET_WAIT_MAX_MS) return null
  if (waitedMs >= fakeLoadMs && readyCheck(bossId)) return null
  return waitedMs + dtMs
}

/**
 * 会話場面の頭で絵を待つ。held=true の間は場面を進めず黒で描く (render は advEntryHolding を見る)。
 * 抜けた時は entryReleased を立てた rt を返す (場面の途中で二度と止めない)。判定が無ければ rt をそのまま返す。
 */
export function holdAdvEntry(rt: AdvRuntime, scene: AdvScene, dtMs: number): { rt: AdvRuntime; held: boolean } {
  if (!advReadyCheck || rt.entryReleased || rt.cursor !== 0 || rt.resultLines || rt.done) return { rt, held: false }
  const waited = rt.entryWaitMs ?? 0
  if (waited >= BATTLE_ASSET_WAIT_MAX_MS || (waited >= fakeLoadMs && advReadyCheck(scene))) return { rt: { ...rt, entryReleased: true }, held: false }
  return { rt: { ...rt, entryWaitMs: waited + dtMs }, held: true }
}

/** 会話場面の頭で絵を待っている最中か (描く側が黒で塗る)。 */
export function advEntryHolding(rt: AdvRuntime | null | undefined): boolean {
  return !!rt && rt.entryWaitMs != null && !rt.entryReleased
}
